import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { usePrefetchQueries } from './hooks/usePrefetchQueries'
import TopBar from './components/TopBar/TopBar'
import MainEditor from './components/Editor/MainEditor'
import NavMenu from './components/TopBar/NavMenu'
import useUiStore from './shared/store/ui.store'
import AudioLibrary from './components/Sidebar/AudioLibrary'
import MasterSection from './components/Editor/MasterSection.tsx'
import LoadingScreen from './components/LoadingScreen.tsx'
import { useMinDelay } from './hooks/useMinDelay.ts'
import { useCheckAuthToken } from './auth/useCheckAuthToken.ts'
import Spinner from './components/common/Spinner.tsx'
import SpeechDialog from './components/SpeechDialog/SpeechDialog'
import './App.css'
import { getAlgoliaApiKey } from '@/features/session/domain/use-cases/get-algolia-api-key.ts'
import { featureFlag } from '@/logic/configuration.ts'
import { useTracking } from './providers/TrackingProvider.tsx'
import { useSaveShortcutNotification } from './hooks/useSaveShortcutNotification.ts'
import OfflineWarningBanner from './components/TopBar/AutoSave/OfflineWarningBanner.tsx'
import { usePreventUnsavedChanges } from './hooks/usePreventUnsavedChanges.ts'
import { useUrlParamSync } from '@/hooks/useUrlParamSync.ts'
import useTracksStore from './shared/store/tracks.store.tsx'
import { useAppStore } from './shared/store/app.store.ts'
import { usePosthogIdentify } from '@/hooks/usePosthogIdentify.ts'
import { useVoiceFiltersStore } from '@/features/voice/store/voice-filters.store'

function App() {
  /**
   * Shows a loading screen for at least 1250ms to avoid flickering.
   * Once the token is ready, it loads the session if one exists.
   * After the loading screen fades out, a secondary loading state appears in the timeline
   * until the session is fully ready.
   */
  const [isAlgoliaApiKeyReady, setIsAlgoliaApiKeyReady] = useState(false)
  const hasWaitedMinTime = useMinDelay(1250)
  const isTokenReady = useCheckAuthToken()

  usePosthogIdentify()

  const [isSessionReady, setIsSessionReady] = useState(false)
  const sessionInStore = useAppStore((state) => !!state.session)
  const { isSidebarOpen, isSpeechDialogOpen } = useUiStore(
    useShallow((state) => ({
      isSidebarOpen: state.isSidebarOpen,
      isSpeechDialogOpen: state.isSpeechDialogOpen,
    }))
  )
  const isMixEmpty = useTracksStore((state) => state.isMixEmpty)
  const isMasterVisible = isSessionReady && !isMixEmpty

  const { isInitialized: isOrgReady, sessionExpected } =
    useUrlParamSync(isTokenReady)

  // Voice search/filters are persisted per-org (see voice-filters.store) —
  // the store hydrates before the org is known, and org can change without a
  // full reload, so reload it whenever the active org changes.
  const org = useAppStore((state) => state.org)
  useEffect(() => {
    void useVoiceFiltersStore.persist.rehydrate()
  }, [org])

  const [loadingState, setLoadingState] = useState<
    'shown' | 'hiding' | 'hidden'
  >('shown')

  const { trackEvent } = useTracking()

  const { View: SaveShortcutNotificationView } = useSaveShortcutNotification()

  useEffect(() => {
    if (!isTokenReady || isSessionReady || !isAlgoliaApiKeyReady || !isOrgReady)
      return

    // When a session is expected (project+session were in URL), wait until the
    // async loadSession resolves and populates the store before marking ready.
    // This prevents the loading overlay from disappearing mid-fetch.
    if (sessionExpected && !sessionInStore) return

    setIsSessionReady(true)
    trackEvent('ProEditor - Session started', {
      userAgent: navigator.userAgent,
      url: window.location.href,
    })
  }, [
    isTokenReady,
    isSessionReady,
    isAlgoliaApiKeyReady,
    isOrgReady,
    sessionExpected,
    sessionInStore,
  ])

  useEffect(() => {
    if (!isTokenReady || !isOrgReady) return

    const algoliaApiKey = featureFlag.forcedAlgoliaProApiKey
    if (algoliaApiKey) {
      setIsAlgoliaApiKeyReady(true)
      return
    }

    getAlgoliaApiKey().then((key) => setIsAlgoliaApiKeyReady(!!key))
  }, [isTokenReady, isOrgReady])

  useEffect(() => {
    if (isTokenReady && hasWaitedMinTime) {
      setLoadingState('hiding')
      const timeout = setTimeout(() => {
        setLoadingState('hidden')
      }, 500)
      return () => clearTimeout(timeout)
    }
  }, [isTokenReady, hasWaitedMinTime])

  usePrefetchQueries({ enabled: isTokenReady && isAlgoliaApiKeyReady })

  // Prevent window/tab close when there are unsaved changes
  usePreventUnsavedChanges()

  return (
    <div className="application">
      <SaveShortcutNotificationView />
      {isTokenReady && (
        <>
          <TopBar isLoading={!isSessionReady} />
          <OfflineWarningBanner />
          <NavMenu isLoading={!isSessionReady} />
          <div className="editor">
            {!isSessionReady && (
              <div className="absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-zinc-900/85">
                <div className="fixed inset-0 mt-[66px] flex items-center justify-center gap-4">
                  <Spinner className="h-8 w-8" />
                  <span>Loading session… This may take a few seconds.</span>
                </div>
              </div>
            )}
            <div className="sidebar" data-open={isSidebarOpen}>
              <div className="wrapper">
                <AudioLibrary />
              </div>
            </div>
            <div className="main">
              <MainEditor isLoading={!isSessionReady} />
            </div>
            <footer data-master-visible={isMasterVisible}>
              <MasterSection isLoading={!isSessionReady} />
            </footer>
          </div>
          {isSpeechDialogOpen && <SpeechDialog />}
        </>
      )}
      {loadingState !== 'hidden' && (
        <div className="absolute inset-0 z-50">
          <LoadingScreen isExiting={loadingState === 'hiding'} />
        </div>
      )}
    </div>
  )
}

export default App
